///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2026
//                           RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { division, laneType, order, userRole, controls } from "./types";
import getUserLaneType from "./getUserLaneType";
import { rxoTlColumns } from "./rxoTlColumns";

/**
 * RXO TL server-paged pricing grid only: same column rules as column order
 * (`!onlyLaneMatch`, not `hide`), plus no lane-match count or delete column.
 */
const SORT_WHITELIST = new Set(
  rxoTlColumns
    .filter(
      c =>
        c.field &&
        !c.onlyLaneMatch &&
        c.control !== controls.hide &&
        c.control !== controls.laneMatch &&
        c.control !== controls.delete
    )
    .map(c => String(c.field).trim().toLowerCase())
);

const DEFAULT_SORT_FIELD = "metaData.defaultIndex";

/**
 * Feature flag + bid type + division + lane type (mirrors backend GetRxoTruckloadLanePricingAccessAsync).
 */
export const shouldUseRxoTlServerPaging = ({
  enableRxoTlPricingServerPagination,
  isCoyoteBid,
  bidDivision,
  effectiveRoleForLanes
}) => {
  if (!enableRxoTlPricingServerPagination) {
    return false;
  }
  if (isCoyoteBid !== false) {
    return false;
  }
  if (bidDivision === division.eu) {
    return false;
  }
  const lt = getUserLaneType(effectiveRoleForLanes);
  return lt === laneType.standardTruckload;
};

export const getEffectiveRoleForLanes = (role, user) =>
  role === userRole.management ? user.userRoleForManagementUser : role;

export const mapSortFieldToApiColumn = sortField => {
  if (!sortField || sortField === DEFAULT_SORT_FIELD) {
    return "";
  }
  const trimmed = String(sortField).trim();
  const normalized = trimmed.toLowerCase();
  return SORT_WHITELIST.has(normalized) ? trimmed : "truckloadLaneId";
};

export const mapSortOrderToApi = sortOrder =>
  sortOrder === order.descending ? "DESC" : "ASC";

/**
 * Builds POST body for `api/v1.0/lanes/rxo-truckload-pricing/search` from bidPricing state.
 */
export const buildRxoTlPricingSearchRequest = ({
  bidId,
  userRoleForLanesWhenManagementUser,
  offset,
  perPage,
  sortField,
  sortOrder,
  columnFilters,
  rangeFilters,
  searchTerm
}) => {
  const pageLength = perPage > 0 ? perPage : 50;
  const pageNumber = Math.floor((offset || 0) / pageLength) + 1;

  const columnFiltersOut = {};
  if (columnFilters) {
    for (const key of Object.keys(columnFilters)) {
      const selected = (columnFilters[key] || [])
        .filter(o => o.selected)
        .map(o =>
          o.value === undefined || o.value === null ? "" : String(o.value)
        );
      if (selected.length > 0) {
        columnFiltersOut[key] = selected;
      }
    }
  }

  const rangeFiltersOut = {};
  if (rangeFilters) {
    for (const key of Object.keys(rangeFilters)) {
      const r = rangeFilters[key];
      if (!r) {
        continue;
      }
      const minRaw = r.minValue;
      const maxRaw = r.maxValue;
      const min =
        minRaw === "" || minRaw === undefined || minRaw === null
          ? null
          : Number(minRaw);
      const max =
        maxRaw === "" || maxRaw === undefined || maxRaw === null
          ? null
          : Number(maxRaw);
      if (
        (min === null || Number.isNaN(min)) &&
        (max === null || Number.isNaN(max))
      ) {
        continue;
      }
      rangeFiltersOut[key] = {
        min: min !== null && !Number.isNaN(min) ? min : null,
        max: max !== null && !Number.isNaN(max) ? max : null
      };
    }
  }

  return {
    bidId,
    userRoleForLanesWhenManagementUser: userRoleForLanesWhenManagementUser ?? 0,
    pageNumber,
    pageLength,
    sortColumn: mapSortFieldToApiColumn(sortField),
    sortOrder: mapSortOrderToApi(sortOrder),
    columnFilters: columnFiltersOut,
    rangeFilters:
      Object.keys(rangeFiltersOut).length > 0 ? rangeFiltersOut : undefined,
    searchTerm: searchTerm != null ? String(searchTerm) : ""
  };
};
